import type { WeeklyTrendPoint } from '@console/types'
import styles from './TrendChart.module.css'

export interface TrendChartProps {
  label: string
  points: readonly WeeklyTrendPoint[]
  formatValue: (value: number) => string
  /** Optional click-through per point. Ask uses it to open the Runs list filtered to that period (spec §5.4). */
  onPointClick?: (index: number) => void
}

const VIEW_WIDTH = 240
const VIEW_HEIGHT = 56
const PADDING_Y = 8

/** Even x-spacing across `n` points, with half a step of margin on each edge so the first/last dot never sits on the chart's border. */
function xFor(index: number, n: number): number {
  if (n <= 1) return VIEW_WIDTH / 2
  const step = VIEW_WIDTH / n
  return step * index + step / 2
}

function yFor(value: number, min: number, max: number): number {
  if (max === min) return VIEW_HEIGHT / 2
  const usable = VIEW_HEIGHT - 2 * PADDING_Y
  return VIEW_HEIGHT - PADDING_Y - ((value - min) / (max - min)) * usable
}

/**
 * Operator-console UX audit finding — `new Date(weekStart).toLocaleDateString()`
 * read the browser's own local calendar with no zone, the same `LGH-024`-class
 * bug already found and fixed on `RunsPage`'s Time column, here twice over (the
 * accessible trend summary and each point's hover title).
 *
 * `weekStart` is a calendar-week boundary the server already fixed, not an
 * instant to convert — `format.ts`'s own `formatInstant` would be needlessly
 * verbose for a chart label ("23 May 2026, 12:53 UTC" for a week marker). This
 * is the same shape as `CustomerPriceListsPage`'s own `formatEffectiveDate`:
 * parsed and rendered in UTC explicitly, so a bare date string can never shift
 * a day either side of midnight depending on the reader's own machine, and
 * `en-GB`'s named month avoids the numeric-order ambiguity `DR-023` documents.
 */
const WEEK_LABEL = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'UTC',
  day: 'numeric',
  month: 'short',
})

function formatWeekStart(iso: string): string {
  const parsed = new Date(iso)
  return Number.isNaN(parsed.getTime()) ? iso : WEEK_LABEL.format(parsed)
}

/**
 * UI spec §6: "Small charts, hairline-ruled, no gradients." One thin
 * accent line, a hairline baseline, and a dot only where the server already
 * flagged an anomaly (`WeeklyTrendPoint.isAnomaly`) — never recomputed here,
 * so the chart can't disagree with whatever number it's showing.
 */
export function TrendChart({ label, points, formatValue, onPointClick }: TrendChartProps) {
  const values = points.map((p) => p.value)
  const min = Math.min(...values, 0)
  const max = Math.max(...values, 1)
  const latest = points[points.length - 1]

  const linePoints = points
    .map((p, i) => `${String(xFor(i, points.length))},${String(yFor(p.value, min, max))}`)
    .join(' ')

  const trendSummary = points
    .map((p) => `${formatWeekStart(p.weekStart)}: ${formatValue(p.value)}`)
    .join('; ')

  return (
    <div className={styles['chart']}>
      <div className={styles['header']}>
        <span className={styles['label']}>{label}</span>
        <span className={styles['current']}>{latest ? formatValue(latest.value) : '—'}</span>
      </div>
      <svg
        viewBox={`0 0 ${String(VIEW_WIDTH)} ${String(VIEW_HEIGHT)}`}
        className={styles['svg']}
        role="img"
        aria-label={`${label}, 12-week trend: ${trendSummary}`}
      >
        <line
          x1={0}
          y1={VIEW_HEIGHT - 1}
          x2={VIEW_WIDTH}
          y2={VIEW_HEIGHT - 1}
          className={styles['baseline']}
        />
        <polyline points={linePoints} className={styles['line']} />
        {onPointClick !== undefined &&
          points.map((p, i) => (
            // A generous invisible target per point — the drawn dot is 3px
            // and only appears on anomalies, so hit areas cannot follow it.
            <rect
              key={`hit-${p.weekStart}`}
              x={xFor(i, points.length) - VIEW_WIDTH / points.length / 2}
              y={0}
              width={VIEW_WIDTH / points.length}
              height={VIEW_HEIGHT}
              fill="transparent"
              className={styles['hit']}
              onClick={() => {
                onPointClick(i)
              }}
            >
              <title>{`${formatWeekStart(p.weekStart)}: ${formatValue(p.value)}`}</title>
            </rect>
          ))}
        {points.map((p, i) =>
          p.isAnomaly ? (
            <circle
              key={p.weekStart}
              cx={xFor(i, points.length)}
              cy={yFor(p.value, min, max)}
              r={3}
              className={styles['anomaly']}
            />
          ) : null,
        )}
      </svg>
    </div>
  )
}
